// Oppie media — motion-aware background-video playback (design.md §4.2, §8).
//
// Background videos render WITHOUT an `autoplay` attribute
// (snippets/oppie-media.liquid), so a still poster shows by default. This module:
//   - starts playback only when motion is allowed AND the section is in view,
//   - pauses off-screen videos (save CPU/battery),
//   - wires the pause/play toggle (WCAG 2.2.2 — control for auto-playing media),
//   - sets a lazy poster (data-poster) just before the video scrolls into view,
//   - on phones swaps the original upload for Shopify's 720p rendition.
// Under prefers-reduced-motion the video stays paused on its poster, but the user
// can still press play. Without JS, the poster simply stays — no CLS, SEO-safe.
//
// iOS Safari refuses play() with NotAllowedError in Low Power Mode, also for a
// muted inline video, until the visitor taps. A refused play() is never final:
//   1. it is retried a few times, on scroll, and when the tab becomes visible;
//   2. a tap or key press starts it (touchend/pointerup/click/keydown carry user
//      activation; touchstart and scroll do not);
//   3. a decorative video (no pause toggle) gets a moving stand-in meanwhile:
//      Safari plays a video/mp4 file in an <img> as an animated image, outside its
//      video-autoplay rules. Shopify serves the original upload as
//      application/octet-stream, which <img> refuses, so the video/mp4 rendition
//      is looked up from Shopify's HLS playlist;
//   4. if that stand-in fails too, a play button appears on the poster.
//
// Lazy poster: a `poster` attribute is fetched by the browser straight away, also
// for a video far below the fold, and that image (a few hundred KB) then competes
// with the page's main image. Below-the-fold videos therefore carry `data-poster`
// (snippets/oppie-media.liquid); it becomes the real poster when the video gets
// within POSTER_ROOT_MARGIN of the viewport. The hero keeps a plain `poster`.
//
// Phone rendition: the theme links the original upload (videos/c/o/v/<id>.mp4),
// because Shopify's renditions are capped at 1080p and the desktop frame deserves
// the full file. On a phone the frame is a few hundred CSS pixels wide, so the
// 720p rendition is visually the same at roughly 60% of the bytes. The URL is
// looked up at setup and the <source> is swapped right before the first play();
// until the lookup is done play() is simply not asked.

const WRAPPER = '[data-oppie-autovideo]';
const TOGGLE = '[data-oppie-video-toggle]';
const START = '[data-oppie-video-start]';
const RETRY_DELAYS_MS = [250, 750, 1500, 3000];
const GESTURE_EVENTS = ['touchend', 'pointerup', 'click', 'keydown'];
const NUDGE_EVENTS = ['scroll', 'pageshow'];
const NUDGE_INTERVAL_MS = 600;
const PREFERRED_RENDITION = 'HD-720p';
const PHONE_RENDITION = 'HD-720p';
const PHONE_MEDIA_QUERY = '(max-width: 749px)';
const POSTER_ROOT_MARGIN = '600px 0px';
const ORIGINAL_UPLOAD_PATTERN = /^(https?:)?\/\/cdn\.shopify\.com\/videos\/c\/o\/v\/([0-9a-f]{32})\.mp4/;

const players = new Set();
const renditionCache = new Map();

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const isPhoneViewport = () => window.matchMedia(PHONE_MEDIA_QUERY).matches;

// Only WebKit (Safari, and every browser on iOS) decodes mp4 in an <img>.
const canAnimateMp4AsImage = () => navigator.vendor === 'Apple Computer, Inc.';

const setPaused = (wrapper, paused) => {
  wrapper.classList.toggle('is-paused', paused);
  const toggle = wrapper.querySelector(TOGGLE);
  if (toggle) {
    toggle.setAttribute('aria-label', paused ? 'Video afspelen' : 'Video pauzeren');
  }
};

// iOS only allows silent autoplay when the element is muted at the moment play()
// is called. The attributes are in the markup already; set the properties too in
// case a script or the browser changed them.
const forceSilentInline = (video) => {
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.setAttribute('playsinline', '');
};

// ── Renditions ──────────────────────────────────────────────────────────────────

const videoSource = (video) => {
  const source = video.querySelector('source');
  return video.currentSrc || (source && source.src) || video.src || '';
};

// cdn.shopify.com/videos/c/o/v/<id>.mp4 (application/octet-stream) →
// cdn.shopify.com/videos/c/vp/<id>/<id>.HD-720p-1.6Mbps-<n>.mp4 (video/mp4),
// read from the HLS playlist Shopify publishes next to every video.
const fetchRendition = async (url, quality) => {
  if (/\/videos\/c\/vp\/[^?]+\.mp4/.test(url)) return url;
  const match = url.match(ORIGINAL_UPLOAD_PATTERN);
  if (!match) return null;
  const id = match[2];
  const base = `https://cdn.shopify.com/videos/c/vp/${id}/`;
  const response = await fetch(`${base}${id}.m3u8`);
  if (!response.ok) return null;
  const pattern = new RegExp(`^${id}\\.([A-Z]+-\\d+p)\\.([\\d.]+Mbps)\\.hls\\.(\\d+)_\\.m3u8$`);
  const renditions = (await response.text())
    .split('\n')
    .map((line) => line.trim().match(pattern))
    .filter(Boolean)
    .map(([, renditionQuality, rate, number]) => ({
      quality: renditionQuality,
      url: `${base}${id}.${renditionQuality}-${rate}-${number}.mp4`,
    }));
  const preferred = renditions.find((r) => r.quality === quality) || renditions[0];
  return preferred ? preferred.url : null;
};

const resolveRendition = (url, quality = PREFERRED_RENDITION) => {
  const key = `${quality} ${url}`;
  if (!renditionCache.has(key)) {
    renditionCache.set(key, fetchRendition(url, quality).catch(() => null));
  }
  return renditionCache.get(key);
};

// ── Moving stand-in (<img> with the mp4 rendition) ─────────────────────────────

// Inline styles, not a class: the element only exists in JS, so a CSS rule for
// it would be stripped by Shopify's live CSS subset.
const createStandIn = (src) => {
  const img = document.createElement('img');
  img.alt = '';
  img.setAttribute('aria-hidden', 'true');
  img.decoding = 'async';
  img.style.cssText = [
    'position:absolute',
    'inset:0',
    'width:100%',
    'height:100%',
    'object-fit:cover',
    'z-index:1',
    'opacity:0',
    'pointer-events:none',
    'transition:opacity var(--dur-base, 240ms) var(--ease-out, ease-out)',
  ].join(';');
  img.src = src;
  return img;
};

// ── Lazy poster ─────────────────────────────────────────────────────────────────

const applyPoster = (video) => {
  const { poster } = video.dataset;
  if (!poster) return;
  delete video.dataset.poster;
  if (!video.poster) video.poster = poster;
};

const observePoster = (player) => {
  const { video, wrapper } = player;
  if (!video.dataset.poster) return;
  if (!('IntersectionObserver' in window)) {
    applyPoster(video);
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      applyPoster(video);
      observer.disconnect();
    },
    { rootMargin: POSTER_ROOT_MARGIN }
  );
  observer.observe(wrapper);
};

// ── Phone rendition ─────────────────────────────────────────────────────────────

// Resolves the rendition URL once per player; play() waits for it
// (player.sourceReady). When the lookup fails the original upload stays in place.
// The <source> itself is only swapped right before the first play(): changing the
// source (video.load()) makes Chrome on Android start downloading despite
// preload="none", also for a video that is hidden or far below the fold.
const preparePhoneSource = (player) => {
  const url = videoSource(player.video);
  if (!isPhoneViewport() || !player.video.querySelector('source') || !ORIGINAL_UPLOAD_PATTERN.test(url)) {
    player.sourceReady = true;
    return;
  }
  resolveRendition(url, PHONE_RENDITION).then((renditionUrl) => {
    player.phoneSourceUrl = renditionUrl;
    player.sourceReady = true;
    if (player.inView) player.attempt();
  });
};

const applyPhoneSource = (player) => {
  const { video, phoneSourceUrl } = player;
  const source = video.querySelector('source');
  if (!phoneSourceUrl || !source || player.phoneSourceApplied) return;
  player.phoneSourceApplied = true;
  if (video.currentSrc.includes('/videos/c/vp/')) return;
  source.src = phoneSourceUrl;
  video.load();
};

// ── Player ──────────────────────────────────────────────────────────────────────

const createPlayer = (wrapper, video) => {
  const player = {
    wrapper,
    video,
    startButton: wrapper.querySelector(START),
    decorative: !wrapper.querySelector(TOGGLE),
    userPaused: prefersReducedMotion(), // reduced-motion → wait for the user
    inView: !('IntersectionObserver' in window),
    sourceReady: false,
    phoneSourceUrl: null,
    phoneSourceApplied: false,
    retryTimer: null,
    refused: false,
    standIn: null,
  };

  player.shouldPlay = () => {
    if (player.userPaused || !player.inView || !player.sourceReady || document.hidden) return false;
    const host = video.closest('oppie-fullscreen-video');
    return !(host && host.isOpen);
  };

  player.setStartVisible = (visible) => {
    if (player.startButton) player.startButton.hidden = !visible;
  };

  player.showStandIn = async () => {
    const src = canAnimateMp4AsImage() ? await resolveRendition(videoSource(video)) : null;
    if (!src || !video.paused) {
      player.setStartVisible(video.paused);
      return;
    }
    const img = createStandIn(src);
    player.standIn = img;
    img.addEventListener('load', () => { img.style.opacity = '1'; }, { once: true });
    img.addEventListener('error', () => {
      img.remove();
      player.standIn = null;
      player.setStartVisible(video.paused);
    }, { once: true });
    video.insertAdjacentElement('afterend', img);
  };

  // First refusal: bridge it for the visitor (stand-in or play button).
  player.onRefused = () => {
    if (player.refused) return;
    player.refused = true;
    if (player.decorative) player.showStandIn();
  };

  player.onPlaying = () => {
    player.setStartVisible(false);
    if (player.standIn) {
      player.standIn.remove();
      player.standIn = null;
    }
  };

  // One play() request. Returns the promise, or null when nothing was asked.
  player.tryPlay = () => {
    if (!player.shouldPlay() || !video.paused) return null;
    applyPoster(video);
    applyPhoneSource(player);
    forceSilentInline(video);
    const promise = video.play();
    return promise && typeof promise.catch === 'function' ? promise : null;
  };

  // A single request without a retry chain (taps, scroll).
  player.nudge = () => {
    const promise = player.tryPlay();
    if (promise) promise.catch(() => {});
  };

  // play() with a short retry chain. Used when the video enters the viewport or
  // the tab becomes visible again.
  player.attempt = (retryIndex = 0) => {
    window.clearTimeout(player.retryTimer);
    const promise = player.tryPlay();
    if (!promise) return;
    promise.catch((error) => {
      // AbortError = we paused it ourselves (scrolled away), not a refusal.
      if (error && error.name === 'AbortError') return;
      if (error && error.name === 'NotAllowedError') player.onRefused();
      const delay = RETRY_DELAYS_MS[retryIndex];
      if (delay === undefined) return; // wait for a tap, scroll or tab switch
      player.retryTimer = window.setTimeout(() => player.attempt(retryIndex + 1), delay);
    });
  };

  return player;
};

const wireControls = (player) => {
  const { video } = player;
  const toggle = player.wrapper.querySelector(TOGGLE);
  if (toggle) {
    toggle.addEventListener('click', () => {
      if (video.paused) {
        player.userPaused = false;
        applyPoster(video);
        applyPhoneSource(player);
        forceSilentInline(video);
        video.play().catch(() => {});
      } else {
        player.userPaused = true;
        video.pause();
      }
    });
  }
  if (player.startButton) {
    player.startButton.addEventListener('click', () => {
      player.userPaused = false;
      applyPoster(video);
      applyPhoneSource(player);
      forceSilentInline(video);
      video.play().catch(() => {});
    });
  }
};

const observeViewport = (player) => {
  if (!('IntersectionObserver' in window)) {
    player.attempt();
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        player.inView = entry.isIntersecting;
        if (entry.isIntersecting) {
          player.attempt();
        } else if (!player.video.paused) {
          player.video.pause();
        }
      });
    },
    { threshold: 0.25 }
  );
  observer.observe(player.wrapper);
};

const setup = (wrapper) => {
  if (wrapper.dataset.oppieVideoReady === 'true') return;
  const video = wrapper.querySelector('.oppie-media__video');
  if (!video) return;

  wrapper.dataset.oppieVideoReady = 'true';
  wrapper.classList.add('is-controllable');
  forceSilentInline(video);

  const player = createPlayer(wrapper, video);
  players.add(player);

  // The toggle icon follows what the video really does, not what we asked for:
  // a refused play() keeps showing the play icon.
  setPaused(wrapper, true);
  video.addEventListener('playing', () => {
    setPaused(wrapper, false);
    player.onPlaying();
  });
  video.addEventListener('pause', () => setPaused(wrapper, true));

  wireControls(player);
  observePoster(player); // also under reduced-motion: the still must show
  preparePhoneSource(player);
  if (player.userPaused) return; // never autostart under reduced-motion
  observeViewport(player);
};

// Must run synchronously inside the gesture handler, otherwise iOS does not count
// the tap as permission to play. Taps on the toggle or play button are left to
// those buttons, so they never fight the visitor's own choice.
const onGesture = (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (target && target.closest(`${TOGGLE}, ${START}`)) return;
  players.forEach((player) => player.nudge());
};

// Scroll fires many times per second. In Low Power Mode every play() is refused
// until a tap, so ask at most once per NUDGE_INTERVAL_MS instead of every frame.
let lastNudge = 0;
const onNudge = () => {
  const now = Date.now();
  if (now - lastNudge < NUDGE_INTERVAL_MS) return;
  lastNudge = now;
  players.forEach((player) => player.nudge());
};

const onVisibilityChange = () => {
  if (document.hidden) return;
  players.forEach((player) => player.attempt());
};

const scan = (root) => {
  const scope = root instanceof Element ? root : document;
  scope.querySelectorAll(WRAPPER).forEach(setup);
};

const init = () => {
  scan(document);
  GESTURE_EVENTS.forEach((type) =>
    document.addEventListener(type, onGesture, { capture: true, passive: true })
  );
  NUDGE_EVENTS.forEach((type) => window.addEventListener(type, onNudge, { passive: true }));
  document.addEventListener('visibilitychange', onVisibilityChange);
  // Theme Editor: re-scan freshly injected sections.
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
